import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Volume2, VolumeX, Phone, ArrowDown } from 'lucide-react';

const HERO_VIDEO_URL = 'https://customer-assets.emergentagent.com/job_b06f7103-614a-40d0-b0be-da5d75ce1ae0/artifacts/7rmz0kvk_tuskermark.mp4';
const HERO_POSTER = '/posters/hero.jpg';

const HeroText = ({ mobile }) => (
  <div className={mobile ? 'px-6 py-10' : 'max-w-[1480px] mx-auto px-12'}>
    {mobile && (
      <div className="overline mb-4 text-[#D4AF37]" data-testid="hero-overline-mobile">Tholpetty, Wayanad — Kerala</div>
    )}
    <h1 className="font-serif text-[#E8E3D9] leading-[0.92]" data-testid="hero-title">
      <span className="block text-[52px] sm:text-[78px] md:text-[104px] lg:text-[128px] font-light tracking-tight md:drop-shadow-[0_2px_24px_rgba(0,0,0,0.6)]">
        Tuskermark
      </span>
      <span className="block text-[22px] sm:text-[32px] md:text-[42px] lg:text-[46px] italic font-light text-[#D4AF37] -mt-1 md:-mt-3 pl-1">
        a safari retreat
      </span>
    </h1>

    <div className="mt-6 md:mt-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-7 lg:gap-8">
      <p className="max-w-lg text-[#E8E3D9]/90 text-[15px] md:text-lg font-light leading-relaxed" data-testid="hero-subtitle">
        A resort hidden inside Tholpetty Wildlife Sanctuary where elephants pass at dawn,
        the forest hums all night, and your only neighbours are wild.
      </p>

      <div className="grid grid-cols-2 gap-6 sm:gap-8 lg:gap-10 shrink-0">
        <div>
          <div className="numeral text-3xl md:text-4xl text-[#D4AF37]">09</div>
          <div className="overline mt-1.5 text-[10px]" style={{ color: '#C4BFB1' }}>Rooms only</div>
        </div>
        <div>
          <div className="numeral text-3xl md:text-4xl text-[#D4AF37]">900m</div>
          <div className="overline mt-1.5 text-[10px]" style={{ color: '#C4BFB1' }}>To the Sanctuary</div>
        </div>
      </div>
    </div>

    <div className="mt-8 flex flex-wrap items-center gap-4">
      <a href="#rooms" className="btn-brass" data-testid="hero-book-btn">
        Reserve your stay
        <ArrowDown size={14} className="rotate-[-45deg]" />
      </a>
      <a href="tel:+918310838799" className="btn-ghost" data-testid="hero-call-btn">
        <Phone size={14} />
        +91 83108 38799
      </a>
    </div>
  </div>
);

const HeroSection = ({ onVideoReady }) => {
  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);
  const [videoReady, setVideoReady] = useState(false);

  const tryAutoplay = useCallback(async (el) => {
    if (!el) return;
    el.muted = true;
    try { await el.play(); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    let io;
    const markReady = () => {
      setVideoReady(true);
      if (onVideoReady) onVideoReady();
      // signal other sections (safari / night videos) to start buffering
      window.__tuskerHeroReady = true;
      window.dispatchEvent(new Event('tusker:hero-ready'));
    };
    if (v) {
      v.addEventListener('loadeddata', markReady);
      v.addEventListener('canplay', markReady);
      tryAutoplay(v);
      // pause hero video when scrolled out of view to reduce CPU/GPU load
      io = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) { v.play().catch(() => {}); } else { v.pause(); } },
        { threshold: 0.1 }
      );
      io.observe(v);
    }
    return () => {
      if (v) { v.removeEventListener('loadeddata', markReady); v.removeEventListener('canplay', markReady); }
      if (io) io.disconnect();
    };
  }, [onVideoReady, tryAutoplay]);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !isMuted ? true : false;
    setIsMuted(!isMuted);
  };

  return (
    <section data-testid="hero-section" className="relative bg-[#0B0F0E] overflow-hidden">
      {/* video stage — natural ratio on mobile, full-bleed cover on desktop */}
      <div className="relative w-full md:h-[88vh] md:min-h-[560px] md:max-h-[920px] md:overflow-hidden">
        <video
          ref={videoRef}
          className="block w-full h-auto bg-[#0B0F0E] md:absolute md:inset-0 md:w-full md:h-full md:object-cover"
          playsInline
          autoPlay
          muted
          loop
          preload="auto"
          poster={HERO_POSTER}
          data-testid="hero-video"
        >
          <source src={HERO_VIDEO_URL} type="video/mp4" />
        </video>

        {/* desktop cinematic overlays */}
        <div className="hidden md:block absolute inset-0 pointer-events-none bg-gradient-to-t from-[#0B0F0E] via-[#0B0F0E]/30 to-[#0B0F0E]/20" />
        <div className="hidden md:block absolute inset-0 pointer-events-none bg-gradient-to-b from-[#0B0F0E]/50 via-transparent to-transparent" />

        {/* desktop top bar — location overline + now-booking badge */}
        <div className="hidden md:block absolute top-0 inset-x-0 z-10 pt-32">
          <div className="max-w-[1480px] mx-auto px-12 flex items-start justify-between gap-4">
            <div className="overline text-[#E8E3D9]/80" data-testid="hero-overline">
              Tholpetty, Wayanad — Kerala
            </div>
            <div className="bg-[#0B0F0E]/50 backdrop-blur-md border border-[#D4AF37]/40 px-5 py-3 max-w-[210px]">
              <div className="overline mb-1 text-[10px]">Now booking</div>
              <div className="font-serif text-base text-[#E8E3D9] italic leading-tight">
                June — July · Monsoon-season Safaris
              </div>
            </div>
          </div>
        </div>

        {/* mute toggle — anchored to bottom-right of the video */}
        <button
          onClick={toggleMute}
          className={`absolute bottom-4 right-4 md:right-12 z-20 w-11 h-11 rounded-full bg-[#0B0F0E]/60 backdrop-blur-md border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0B0F0E] transition-all ${videoReady ? 'opacity-100' : 'opacity-0'}`}
          data-testid="mute-toggle-desktop"
          aria-label="Toggle mute"
        >
          {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>

        {/* desktop bottom-aligned text overlay */}
        <div className="hidden md:block absolute bottom-0 inset-x-0 z-10 pb-16">
          <HeroText />
        </div>
      </div>

      {/* mobile text — flows below the natural-ratio video */}
      <div className="md:hidden bg-[#0B0F0E]">
        <HeroText mobile />
      </div>

      {/* wildlife marquee tail */}
      <div className="border-y border-[#D4AF37]/15 overflow-hidden py-4 bg-[#0B0F0E]">
        <div className="flex animate-marquee whitespace-nowrap">
          {Array(2).fill(['Indian Elephant','Bengal Tiger','Indian Leopard','Sloth Bear','Sambar Deer','Wild Boar','Malabar Hornbill','Indian Bison','Spotted Deer','Langur','Civet','Mongoose']).flat().map((animal, i) => (
            <span key={i} className="mx-10 text-sm tracking-[0.3em] uppercase text-[#8E9A95]">
              {animal}
              <span className="ml-10 text-[#D4AF37]">✦</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
